import { useCallback, useMemo, useRef, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';

import { cn } from '@/lib/utils';

import { useFileDrop } from '../model/use-file-drop';
import { extensionsToAccept, validateUploadFile } from '../model/validate-file';

interface FileUploadProps {
  /** HTML `accept` hint for the picker. Defaults to a value derived from `allowedExtensions`. */
  accept?: string;
  /** Enforced allowlist of extensions, e.g. `['.xlsx']`. Undefined / empty = accept any type. */
  allowedExtensions?: string[];
  /** Enforced maximum file size in bytes. Undefined = no limit. */
  maxSize?: number;
  multiple?: boolean;
  onFilesSelected?: (files: FileList) => void;
  /** Called with a message when a selected / dropped file fails validation. */
  onError?: (error: string) => void;
  className?: string;
  disabled?: boolean;
}

export function FileUpload({
  accept,
  allowedExtensions,
  maxSize,
  multiple = true,
  onFilesSelected,
  onError,
  className,
  disabled = false
}: FileUploadProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const acceptAttr = useMemo(() => accept ?? extensionsToAccept(allowedExtensions), [accept, allowedExtensions]);

  const handleFiles = useCallback(
    (files: FileList) => {
      if (!files || files.length === 0) return;

      for (let i = 0; i < files.length; i++) {
        const result = validateUploadFile(files[i], { allowedExtensions, maxSize });
        if (!result.valid) {
          // Reject the whole selection if any file fails validation.
          onError?.(result.error ?? 'Invalid file');
          return;
        }
      }

      onFilesSelected?.(files);
    },
    [allowedExtensions, maxSize, onFilesSelected, onError]
  );

  const { isDragging, handleDragOver, handleDragLeave, handleDrop } = useFileDrop({
    onFilesSelected: handleFiles,
    disabled
  });

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
      }
    },
    [handleFiles]
  );

  const handleClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  const uploadClasses = cn(
    'flex flex-col items-center justify-center gap-4 px-6 py-8',
    'bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg',
    'cursor-pointer transition-all duration-200 select-none',
    'hover:bg-gray-100 hover:border-primary-600',
    'focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-2',
    {
      'bg-primary-50 border-primary-500': isDragging,
      'opacity-50 cursor-not-allowed': disabled
    },
    className
  );

  return (
    <div
      className={uploadClasses}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={acceptAttr}
        multiple={multiple}
        onChange={handleFileChange}
        disabled={disabled}
        aria-hidden="true"
      />

      <Upload className="text-gray-600" size={40} />

      <div className="flex flex-col items-center gap-2">
        <span className="text-sm text-gray-600 text-center">{t('gen.drage.file.label')}</span>
      </div>
    </div>
  );
}
